import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';

export enum LogLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3,
}

export interface LoggerConfig {
  /** Minimum log level to output */
  level?: LogLevel;
  /** Enable/disable remote logging */
  enableRemote?: boolean;
}

export const LOGGER_CONFIG = 'LOGGER_CONFIG';

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private minLevel = LogLevel.Info;
  private sendToBackend = true;
  private enableConsole = true;

  constructor(
    private http: HttpClient,
    @Optional() @Inject(LOGGER_CONFIG) config?: LoggerConfig
  ) {
    const isLocalhost = window.location.hostname === 'localhost';
    this.minLevel = config?.level ?? (isLocalhost ? LogLevel.Debug : LogLevel.Info);
    this.enableConsole = isLocalhost; // Console only on localhost
    this.sendToBackend = config?.enableRemote ?? !isLocalhost; // Don't send to backend on localhost
  }

  debug(message: string, data?: unknown): void {
    this.logMessage(LogLevel.Debug, message, data);
  }

  info(message: string, data?: unknown): void {
    this.logMessage(LogLevel.Info, message, data);
  }

  warn(message: string, data?: unknown): void {
    this.logMessage(LogLevel.Warn, message, data);
  }

  error(message: string, error?: unknown): void {
    this.logMessage(LogLevel.Error, message, error);
  }

  private logMessage(level: LogLevel, message: string, data?: unknown): void {
    this.log(level, message, data);
    if (this.sendToBackend) {
      this.sendToRemote(message, data, level);
    }
  }

  private log(level: LogLevel, message: string, data?: unknown): void {
    if (level < this.minLevel) return;

    if (this.enableConsole) {
      const timestamp = new Date().toISOString();
      const prefix = `${timestamp} [${LogLevel[level]}]`;

      const consoleMethods: Record<LogLevel, (...args: unknown[]) => void> = {
        [LogLevel.Debug]: console.debug.bind(console),
        [LogLevel.Info]: console.info.bind(console),
        [LogLevel.Warn]: console.warn.bind(console),
        [LogLevel.Error]: console.error.bind(console),
      };

      consoleMethods[level](prefix, message, data ?? '');
    }
  }

  private sendToRemote(message: string, data?: unknown, level?: LogLevel): void {
    setTimeout(() => {
      this.http
        .post('/v1/internal_log', {
          message,
          error: level === LogLevel.Error ? data : undefined,
          warning: level === LogLevel.Warn ? data : undefined,
          info: level === LogLevel.Info ? data : undefined,
          debug: level === LogLevel.Debug ? data : undefined,
          level: level !== undefined ? LogLevel[level] : undefined,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        })
        .subscribe({
          error: () => {
            //remote logging errors should not break the app
          },
        });
    }, 0);
  }

  // Optional runtime configuration
  setLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  enableRemote(enabled: boolean): void {
    this.sendToBackend = enabled;
  }
}
